import type {FileListRefusal} from "api/apiErrorIds";
import {overLimitPricingOfRule, type OverLimitPricingRule} from "./OverLimitApi";
import {offeredPlanId} from "./overLimitPayDialog";
import type {ComposerMode} from "../request/composerMode";

/**
 * The over-limit walls as the composer reasons about them (kitecorp/transferify-ui#1454, api
 * kitecorp/transferify-api-service#823): what a FREE sender over the free cap meets, and the settled purchase
 * that still pays for a send.
 *
 * No one-off is sold any more (plan W2; the ui sells none since kitecorp/transferify-ui#3800, and its purchase
 * path is gone since kitecorp/transferify-ui#3916): the way past either wall is a plan. A purchase bought before
 * then still pays for one send, so the quote, the purchase read and the `paid` rung stay.
 *
 * Plain TypeScript — this module imports no React — because every rule here reads an answer the SERVER
 * owns. The two BOUNDARIES are the api's — `freeCapBytes` and `maxSellableBytes` ride on every offer for
 * exactly that reason — so this module never restates 5 GB or 1 TB.
 */

/**
 * Which rule prices a way past the free cap (api kitecorp/transferify-api-service#1039) — the delivery
 * path reduced to the only thing the money layer needs to know about it.
 *
 * HOSTED scales with the size because the bytes sit in our storage for the transfer's retention window.
 * INSTANT carries no charge since kitecorp/transferify-api-service#3005: the bytes never leave the sender's device, so
 * there is nothing to meter, and a FREE sender over the free size needs a plan. The answers are the api's; this type
 * only decides which question to ask it, and which sentence answers the sender.
 */
export type OverLimitPricing = "hosted" | "instant";

/**
 * `OverLimitBreakdown` on the wire: how the quoted total is composed, line by line.
 *
 * The per-unit lines and the flat line are MUTUALLY EXCLUSIVE, and the api guarantees the three sum to
 * the offer's `priceMinor`. A hosted offer is either on the 10 GB ladder (`flatRateUnits: 0`) or at the
 * flat minimum (`flatRateUnits: 1` with both unit counts zero); an instant offer is always the flat one.
 * Both per-unit AMOUNTS travel either way, so nothing here has to ask again to render a rate.
 */
export interface OverLimitBreakdown {
	unitBytes: number;
	units: number;
	firstRateUnits: number;
	firstUnitAmountMinor: number;
	beyondRateUnits: number;
	beyondUnitAmountMinor: number;
	/** `1` when this offer charges its flat line, else `0`. */
	flatRateUnits: number;
	/** What that flat line costs: the hosted minimum, or the instant path's whole price. */
	flatUnitAmountMinor: number;
}

/**
 * `OverLimitCredit` on the wire (kitecorp/transferify-api-service#2787): the caller's unused purchase that pays for
 * PART of this send. When an offer carries one, its `priceMinor` is ALREADY the difference the checkout charges.
 */
export interface OverLimitCredit {
	/** What the purchase counts for, in `currency`'s minor units. */
	paidMinor: number;
	currency: string;
	/** The largest send that payment pays for on this send's path, read from the live tariff. */
	coversBytes: number;
}

/** `OverLimitOffer` on the wire — what a declared size costs this caller on this storefront. */
export interface OverLimitOffer {
	/** This size needs paying for: the caller is FREE and over the deployed free cap. */
	required: boolean;
	/** This storefront can actually SELL it. False above the ceiling, and for an unpriced currency. */
	available: boolean;
	/** The VAT-inclusive total in `currency`'s MINOR units, or null when nothing is required or sold. */
	priceMinor?: number | null;
	/** Lower-case ISO code of the storefront's own currency, never converted. */
	currency?: string | null;
	/** The per-unit composition of `priceMinor`. Rendered, never recomputed. */
	breakdown?: OverLimitBreakdown;
	/** The size at or below which a transfer is free, as the DEPLOYED catalogue defines it. */
	freeCapBytes: number;
	/** The largest transfer the api quotes. Above it nothing is offered, and the answer is a smaller selection. */
	maxSellableBytes: number;
	/**
	 * The pay dialog's plan row (kitecorp/transferify-ui#3229): the plan that lifts this wall, its first month at a
	 * fixed price and the monthly price it renews at. Absent wherever the api offers no plan for this send, which
	 * leaves the wall a sentence with no control. Every figure is the api's and is rendered, never computed.
	 */
	planOffer?: OverLimitPlanOffer;
	/**
	 * W2's answer (kitecorp/transferify-api-service#3017): every paid plan that holds the drop, smallest first, the first
	 * preselected. Absent on an api from before W2, which sends the single `planOffer` instead; the dialog reads either
	 * ({@link ./sizedPlanDialog.fittingPlanOffers}).
	 */
	planOffers?: readonly OverLimitPlanOffer[];
	/** The unused purchase `priceMinor` is the difference to, or absent when none counts toward this send. */
	credit?: OverLimitCredit | null;
}

/** `OverLimitPlanOffer` on the wire: the pay dialog's plan row (kitecorp/transferify-ui#3229). */
export interface OverLimitPlanOffer {
	/** The plan offered, as the catalogue codes it. */
	planCode: "FREE" | "BASIC" | "PLUS" | "PREMIUM";
	/** Its monthly Stripe Price, which the plan checkout names. */
	priceId: string;
	/** What the first invoice charges, VAT-inclusive, in `currency`'s minor units. */
	firstMonthMinor: number;
	/** The monthly price every later invoice charges. */
	renewalMinor: number;
	/** Lower-case ISO code, the same as the quote's own on the same answer. */
	currency: string;
	/** The plan's per-transfer hosted limit, which the row states; null for none. */
	maxHostedUploadBytes?: number | null;
}

/** `OverLimitAllowance` on the wire: a settled purchase awaiting its transfer. Always CONFIRMED. */
export interface OverLimitAllowance {
	allowanceId: string;
	/** The size this purchase admits — the figure that was quoted, echoed so a grown selection is caught. */
	allowanceBytes: number;
	amountMinor: number;
	currency: string;
}

/**
 * What the over-cap selection MEANS for this sender — the whole of ui#1454's "dead end becomes a priced
 * path", expressed as one value rather than as conditions spread across the panel.
 *
 * Ten outcomes because they are ten different surfaces or funnel facts, and collapsing any pair
 * renders the wrong one to somebody:
 *
 * - `clear` — nothing stands in the way: within the cap, not a hosted route, or the server says this
 *   size needs no charge (which is how a SUBSCRIBED sender is answered — see {@link resolveOverLimit}).
 * - `upgradeOnly` — over the cap on a PAID plan. The honest answer there is the upgrade nudge, the only rung that
 *   shows it.
 * - `checkingPurchase`: over the cap on FREE, signed in, and the account has not yet said whether it
 *   already holds a purchase that covers this send (kitecorp/transferify-ui#3010). It renders like
 *   `quoting` but is NOT merged with it: `quoting` is a paywall the funnel counts as shown, and this
 *   rung is shown to senders who have already paid. Before it existed, "not asked yet" and "holds
 *   nothing" were the same `null`, so a composer that came back after a refused upload start priced a
 *   send its CONFIRMED allowance covered whenever the quote won the race with the purchase read.
 * - `quoting` — over the cap on FREE and the quote has not arrived yet. NOT merged with the answer it is
 *   waiting for: rendering a CTA before the quote is in is what offers a sender a plan the api then refuses.
 * - `signInRequired` — over the cap with no account session. An allowance BELONGS TO AN ACCOUNT, so a
 *   signed-out visitor can neither be quoted nor buy, and this rung is what holds the quote back until
 *   they sign in. It RENDERS NOTHING and does not hold Send shut (owner decision 2026-10-09,
 *   kitecorp/transferify-ui#3435): the sender already has to give an e-mail to send, the usual Send opens the
 *   e-mail code step that signs them in, and the priced wall takes over from there. Kept apart from
 *   `quoting` because the two are not the same state: one is waiting for an answer, this one is waiting
 *   for the sender, and rendering it as `quoting` is a spinner that never resolves (the 2026-08-30 prod
 *   defect — see {@link resolveOverLimit}). The funnel still counts reaching it
 *   ({@link overLimitSignedOutReached}). A session whose account read failed and whose sign-in identity carries
 *   no e-mail meets this rung too (`firebaseSession.fromFirebaseIdentity`); its Send reaches the upload init,
 *   which the api answers with its own over-cap refusal.
 * - `quoteFailed` — the price was asked for, re-asked its whole budget, and never arrived. Also its
 *   own rung for the same reason: without it a failed quote is indistinguishable from a pending one,
 *   and the surface holds a spinner over a request that has already given up.
 * - `paid` — a settled allowance covers this size; the send proceeds and names it on the init.
 * - `fileListRefused` — the upload init would refuse this selection's file LIST whatever was paid
 *   (kitecorp/transferify-ui#2930, api kitecorp/transferify-api-service#2144): more files than a transfer may
 *   hold, or a name or content type too long. No payment step is offered; the rung names the rule and the
 *   sender's way forward, which is a smaller or zipped selection.
 * - `aboveLargestPlan`: a HOSTED selection above the largest paid plan's cap (kitecorp/transferify-api-service#3005),
 *   or above the ceiling the api's quote publishes (`maxSellableBytes`): the api sells no plan at that size and refuses
 *   it for every tier, so no payment step and no upgrade nudge is offered (an upgrade could not hold it). The rung
 *   names the limit and the sender's way forward, a smaller selection or several transfers. It follows the sign-in
 *   (a signed-out sender meets `signInRequired` first, #3435), and a settled purchase that already covers the size
 *   outranks it, because that purchase was quoted before the api closed the size and the init still honours it.
 * - `planRequired`: the wall of a FREE send over the free size, hosted or Instant (kitecorp/transferify-ui#3800), and
 *   what the storage wall's quote reads as too. A plan is the one way past it: the one-off is not sold (Instant:
 *   kitecorp/transferify-api-service#3005, Q5; hosted: Q3, "plans only", carried out by Ruling 1), whatever price an
 *   older api still puts on its quote. On the size wall {@link ./sizedPlanDialog.plansOnlyResolution} fills the rung
 *   with the first row of the plans that fit the drop, or null on a storefront where no row is wired; the pay dialog
 *   lists every fitting row, with the smallest chosen ({@link ./sizedPlanDialog.chosenSizedPlan}). The storage wall
 *   offers no plan row and no pass (`storagePassPaid` is false there). It follows the same sign-in and purchase
 *   checks as every rung that answers a quote, because a settled purchase still pays for the send on both walls.
 */
export type OverLimitResolution =
	| {kind: "clear"}
	| {kind: "upgradeOnly"}
	| {kind: "checkingPurchase"}
	| {kind: "quoting"}
	| {kind: "signInRequired"}
	| {kind: "quoteFailed"}
	| {kind: "paid"; allowanceId: string}
	| {kind: "fileListRefused"; rule: FileListRefusal}
	| {kind: "aboveLargestPlan"}
	| {kind: "planRequired"; planOffer: OverLimitPlanOffer | null};

export interface OverLimitInput {
	/**
	 * Which rule prices this send ({@link ../components/createFormLogic.overLimitPricingOf}).
	 *
	 * Every send is one or the other, which is why this replaced the old `hostedUpload` boolean: an
	 * instant selection used to mean "no cap applies here" and now means "a different price applies
	 * here", and a boolean that answered the first question could not be widened to answer the second.
	 */
	pricing: OverLimitPricing;
	/** The selection exceeds the viewer's own plan cap — the existing client-side size fact. */
	overCap: boolean;
	/**
	 * FREE is the only tier a plan wall is raised for, on either delivery path.
	 *
	 * What a paid tier meets instead depends on the path, and that is the api's asymmetry rather than a
	 * preference here: hosted keeps the upgrade nudge because its init still refuses a subscriber over
	 * their own cap, while instant shows nothing because its registration refuses nothing.
	 */
	isFreePlan: boolean;
	/**
	 * The sender holds an ACCOUNT session — not merely a Firebase one.
	 *
	 * An anonymous session does not count and must not: the api's over-limit routes are account-scoped
	 * because the allowance they mint belongs to an account, so an anonymous caller has nothing to buy
	 * a purchase against.
	 */
	signedIn: boolean;
	/** The total selected bytes, as the api was asked to quote. */
	selectedBytes: number;
	/** The server's quote, or null while it is outstanding / was never asked for. */
	offer: OverLimitOffer | null;
	/** The account's settled purchase covering this size, or null. */
	allowance: OverLimitAllowance | null;
	/**
	 * The account has been asked, on this delivery path, whether it holds a settled purchase, and the
	 * answer is in: a purchase, none, or a failed read ({@link ./useOverLimitPurchase}).
	 *
	 * What gives {@link allowance}'s `null` its meaning. Without it `null` meant both "nothing held"
	 * and "not asked yet", and only the first of those may be priced (kitecorp/transferify-ui#3010).
	 */
	allowanceAnswered: boolean;
	/** The quote was asked for, re-asked its whole budget, and never arrived ({@link ./useOverLimitPurchase}). */
	quoteFailed: boolean;
	/**
	 * The file-list rule this selection breaks, or null: the composer's own count check
	 * ({@link ./fileListRules.fileListRefusedLocally}), or else the rule the api refused the quote or the checkout
	 * under ({@link ./useOverLimitPurchase}).
	 */
	fileListRefusal: FileListRefusal | null;
	/**
	 * The HOSTED selection is above the largest paid plan's cap, from the baked catalogue
	 * ({@link ../components/createFormLogic.isHostedSelectionAboveEveryPlan}) or the quote's own refusal
	 * ({@link ./useOverLimitPurchase}); never true on an Instant link, which stores no bytes.
	 */
	aboveLargestPlan: boolean;
}

/**
 * Reads the walls off the server's answers.
 *
 * `offer.required` is the AUTHORITY on whether anything is owed, and deliberately so: it is the only
 * figure computed against the DEPLOYED catalogue, while `overCap` is computed against the baked one.
 * When they disagree the server wins — a baked cap that has fallen behind a deployment then quotes
 * nobody, instead of pitching a purchase the api would refuse.
 *
 * NO RUNG MAY BE A DEAD END, which is the rule the 2026-08-30 prod defect broke. A signed-out visitor
 * dropping an over-cap selection resolved to `quoting` — the composer never asks for a quote it has no
 * audience for, so no answer was coming — and `quoting` renders a sentence and no control, while
 * {@link overLimitBlocksSend} disabled the Send behind it. The result was a permanent
 * "working out the price…" with no error and no sign-in prompt, on the first screen paid traffic
 * lands on. `signInRequired` and `quoteFailed` exist so that every reachable rung either renders the
 * sender's own way forward, or leaves the usual Send open as that way forward
 * (`signInRequired`, kitecorp/transferify-ui#3435); a new rung added to the union owes the same.
 *
 * A SUBSCRIBED SENDER NEVER REACHES THE CHECKOUT, and that is guarded twice on purpose. Here,
 * `isFreePlan` diverts every paid tier away before any offer is consulted; and at the fetch, the
 * composer never asks for a quote it has no audience for. The api holds the same line a third time
 * (`startAllowance` refuses a non-FREE caller), so the constraint survives any one of the three being
 * edited.
 *
 * THE INSTANT PATH ENDS IN THE PLAN WALL, NOT A PRICE (kitecorp/transferify-api-service#3005, Q5). The Instant one-off is
 * no longer sold: the registration refuses a FREE sender past the free cap whatever the deployment sells
 * (`TRANSFER_INSTANT_PLAN_REQUIRED`), so there is no price to quote and no send to wave through. It shares every rung
 * above it with the hosted path (the sign-in, the settled purchase that still pays for an Instant send, the wait for the
 * purchase read), and what is left once those have answered is `planRequired`, carrying the plan row the api offers or
 * null where none is wired. This replaces the dark-ship exit (ui#1719), which cleared an Instant send the api had nothing
 * to sell for because the registration then refused nothing. It refuses that send now, so clearing it would leave the
 * sender pressing Send into a refusal. Hosted still falls through to its own terminal refusal (`assertWithinPlanLimits`)
 * when nothing is for sale.
 *
 * NOTHING IS PRICED BEFORE THE ACCOUNT HAS SAID WHETHER IT ALREADY PAID (kitecorp/transferify-ui#3010).
 * A covering purchase outranks every answer the quote can give, so until the purchase read answers,
 * none of those answers is final. The composer asks both questions at the same moment and the two
 * race; in production the quote won after every refusal (PRICE_SEEN a second later each time). When
 * the missing answer was read as "holds nothing", a composer that came back after a refused upload
 * start put the strip's pay action (then labelled "Pay … & send") beside a CONFIRMED allowance, and the
 * funnel recorded the offer as shown and priced again.
 */
export function resolveOverLimit(
	{pricing, overCap, isFreePlan, signedIn, selectedBytes, offer, allowance, allowanceAnswered, quoteFailed,
		fileListRefusal, aboveLargestPlan}: OverLimitInput,
): OverLimitResolution {
	if (!overCap) {
		return {kind: "clear"};
	}
	// A SELECTION THE UPLOAD INIT WOULD REFUSE IS OFFERED NO PAYMENT STEP (kitecorp/transferify-ui#2930). It outranks
	// every answer below that leads to a payment or claims the send can go: the upgrade nudge on a paid plan (a
	// subscription checkout sold for this send), the signed-out rung (whose open Send leads into a sign-in to pay for
	// it), `paid` ("ready to send") and every priced rung. None of that is true of a list the init refuses whatever was paid (the
	// 2026-09-24 buyer paid twice). A paid plan sending INSTANTLY keeps `clear` below: nothing is sold there.
	if (fileListRefusal !== null && (isFreePlan || pricing === "hosted")) {
		return {kind: "fileListRefused", rule: fileListRefusal};
	}
	// A paid tier is answered here, BEFORE the upgrade nudge: no plan holds this size, so the nudge would point at a
	// plan that cannot take it. A FREE sender is answered below, after the sign-in and the purchase checks.
	if (aboveLargestPlan && !isFreePlan) {
		return {kind: "aboveLargestPlan"};
	}
	if (!isFreePlan) {
		// The upgrade nudge belongs to the path that still has a cap for it to point past. A subscriber
		// sending INSTANTLY is over nothing the api measures, so nudging them here would invent a limit.
		return pricing === "hosted" ? {kind: "upgradeOnly"} : {kind: "clear"};
	}
	// BEFORE any offer or allowance is consulted, and it outranks both. Signing out does not unmount
	// the composer, so a previous session's answers can still be in hand — and neither can be acted on
	// without an account, so rendering a price here would put a CTA on screen that mints against nobody.
	if (!signedIn) {
		return {kind: "signInRequired"};
	}
	// BEFORE the allowance, deliberately. `required: false` is the server saying this send owes nothing —
	// which happens to an account that already holds a settled purchase the moment the deployed free cap
	// rises above their selection. Checking coverage first would return `paid`, name the allowance on the
	// init, and spend a purchase on a transfer that had become free. A cached allowance still reaches
	// `paid` below whenever no offer has arrived to say otherwise.
	if (offer !== null && !offer.required) {
		return {kind: "clear"};
	}
	if (allowanceCovers(allowance, selectedBytes)) {
		return {kind: "paid", allowanceId: allowance!.allowanceId};
	}
	if (!allowanceAnswered) {
		// Every answer below is one a covering purchase would overturn, so none of them is rendered
		// until the account has been asked. The read always settles, a failure included, so this rung
		// resolves itself and is never a dead end.
		return {kind: "checkingPurchase"};
	}
	// No quote or checkout can follow: the api sells nothing at this size, so the quote (if one was asked for) is
	// not waited on either.
	if (aboveLargestPlan) {
		return {kind: "aboveLargestPlan"};
	}
	if (offer === null) {
		// The quote is either still coming or has given up. Both render a sentence and no price, and
		// telling them apart is the whole point: one is worth waiting for and the other needs the
		// sender to re-ask. `quoteFailed` is read only HERE, after the answers that outrank it — a
		// settled allowance still sends, and a server that says nothing is owed still clears, however
		// the last quote went.
		return quoteFailed ? {kind: "quoteFailed"} : {kind: "quoting"};
	}
	// THE INSTANT PATH SELLS NO ONE-OFF (kitecorp/transferify-api-service#3005, Q5), so whatever the quote says about a
	// price (a figure from an api that predates kitecorp/transferify-api-service#3005 included) the way past the free cap is the plan the api offers,
	// or nothing on a storefront where that row is not wired. Decided before the hosted branch below, which reads the
	// quote's availability and would otherwise treat an Instant send above the ceiling as a refusal for every tier.
	if (pricing === "instant") {
		return {kind: "planRequired", planOffer: offer.planOffer ?? null};
	}
	// Above the ceiling and "not sold" arrive as the SAME `available: false` from the api, which collapses them on
	// purpose. The ui can still tell them apart without recomputing anything, because `maxSellableBytes` travels on
	// every offer — and it must, since the two need opposite answers: above it the way forward is a smaller selection,
	// below it a plan.
	if (!offer.available && selectedBytes > offer.maxSellableBytes) {
		return {kind: "aboveLargestPlan"};
	}
	// THE HOSTED PATH SELLS NO ONE-OFF EITHER (plan W2; kitecorp/transferify-ui#3916): a quote that is available and
	// priced, from an api that still prices it, is answered as one that is not sold. The plan the api offers beside it
	// is the way past the cap.
	return {kind: "planRequired", planOffer: offer.planOffer ?? null};
}

/**
 * Whether a settled purchase admits this selection.
 *
 * The api already filters by size (`GET /allowance?bytes=` answers 204 when nothing covers), so this
 * is the second reading rather than the only one — and it earns its place because the SELECTION CAN
 * GROW after payment. `allowanceBytes` is echoed on the checkout handoff for exactly this: a sender who
 * pays for 6 GB and then adds a file must meet the quote again here, not a 400 at the upload init.
 */
export function allowanceCovers(allowance: OverLimitAllowance | null, selectedBytes: number): boolean {
	return allowance !== null && allowance.allowanceBytes >= selectedBytes;
}

/**
 * A one-off purchase the reader pressed "Use it" on (kitecorp/transferify-ui#3004), as the composer
 * needs it: its id, the size it was quoted (null where the listing stated none), what
 * was paid, and the type it was bought for as the listing spells it (null for a listing that stated
 * none, and also for a spelling this build does not know). Structural rather than the account
 * module's own type, so this rules module keeps importing nothing from the account slice.
 */
export interface ChosenOneOffPurchase {
	purchaseId: string;
	allowanceBytes: number | null;
	amountMinor: number;
	currency: string;
	pricing: OverLimitPricingRule | null;
}

/**
 * The allowance the upload init names when the reader has CHOSEN one (kitecorp/transferify-ui#3004).
 *
 * The api's `GET /allowance?bytes=` answers its best-fit candidate, the cheapest settled purchase that pays for
 * the send, and that stays the answer whenever nothing was chosen, or this side
 * cannot vouch that the api would take the choice. Since kitecorp/transferify-api-service#2787 a purchase pays
 * for one send of either type for what was paid, and never for less than the size it was quoted on the type it
 * was bought for (the owner's rules 5 and 9 of 2026-10-17; the api's `docs/pricing/over-limit-one-off.md`,
 * "When a purchase is used"). Two arms of that are readable from the listing:
 *
 * - the floor: a choice bought for this send's type, quoted at least this size;
 * - a hosted choice for an instant send, of any size: an instant send costs the least hosted price at every
 *   size, so any hosted purchase pays for it.
 *
 * What a choice's money buys beyond those (an instant purchase on a hosted send, a hosted one above its quoted
 * size) is the api's to compute from the live catalogue, so the candidate stays rather than an init the api
 * could refuse with `error.transfer.overLimit.notConsumable`.
 */
export function preferredOverLimitAllowance(
	candidate: OverLimitAllowance | null,
	chosen: ChosenOneOffPurchase | null,
	selectedBytes: number,
	pricing: OverLimitPricing,
): OverLimitAllowance | null {
	if (candidate === null || chosen === null || chosen.allowanceBytes === null
		|| chosen.purchaseId === candidate.allowanceId) {
		return candidate;
	}
	const boughtFor = overLimitPricingOfRule(chosen.pricing);
	const floorCovers = boughtFor === pricing && chosen.allowanceBytes >= selectedBytes;
	const hostedPaysForInstant = boughtFor === "hosted" && pricing === "instant";
	if (!floorCovers && !hostedPaysForInstant) {
		return candidate;
	}
	return {
		allowanceId: chosen.purchaseId,
		allowanceBytes: chosen.allowanceBytes,
		amountMinor: chosen.amountMinor,
		currency: chosen.currency,
	};
}

/**
 * The purchase to hold after a read for `askedBytes` came back with `read`.
 *
 * The api filters `GET /allowance?bytes=` BY SIZE, so "nothing" means two different things and only one
 * of them concerns the purchase already in hand:
 *
 * - nothing at a size the held purchase COVERS is the api saying that purchase is gone — spent by
 *   another tab, or refunded — and continuing to offer it would send a transfer the api then refuses
 *   with `error.transfer.overLimit.notConsumable`;
 * - nothing at a LARGER size says nothing about it at all. A sender who paid for 6 GB and then added a
 *   file is legitimately quoted again for 200 GB, and dropping their 6 GB purchase on that answer would
 *   strand them the moment they removed the file again.
 *
 * Lives here rather than in the hook because it is the same kind of rule as {@link allowanceCovers}, and a
 * copy of this reasoning beside each read is how one of them ends up dropping a purchase the sender paid for.
 */
export function allowanceAfterRead(
	held: OverLimitAllowance | null,
	read: OverLimitAllowance | null,
	askedBytes: number,
): OverLimitAllowance | null {
	if (read !== null) {
		return read;
	}
	return allowanceCovers(held, askedBytes) ? null : held;
}

/**
 * The `overLimitAllowanceId` an upload init should carry, or null to send none.
 *
 * A PAID ALLOWANCE IS NEVER AUTO-APPLIED — the init NAMES it (api#823), so a 1 TB purchase can never be
 * quietly spent on a 6 GB send. `null` is therefore a first-class answer: every resolution but `paid`
 * has nothing to spend, and naming an allowance that does not cover is refused with
 * `error.transfer.overLimit.notConsumable`.
 */
export function overLimitAllowanceId(resolution: OverLimitResolution): string | null {
	return resolution.kind === "paid" ? resolution.allowanceId : null;
}

/**
 * Which action the strip's ONE control performs on this rung, or null where it offers none.
 *
 * The owner's constraint is a single CTA, so the surface renders ONE control and this decides what it
 * does — rather than the view growing a button per rung, which is both the constraint broken and what
 * `scripts/over-limit-single-cta-sweep.mjs` counts. Exhaustive over the union on purpose: a rung added
 * later must state here whether it offers the sender an action, and a rung that offers none must leave
 * the sender another way out, or it is a rung they cannot leave, which is the defect ui#1715 fixed.
 */
export type OverLimitAction = "retry" | "plan";

export function overLimitAction(resolution: OverLimitResolution): OverLimitAction | null {
	switch (resolution.kind) {
		case "quoteFailed":
			return "retry";
		case "planRequired":
			// The plan press only where the api offered a paid plan. Without the row the strip states the wall alone, and
			// the sender's way forward is a smaller selection, as it is on `aboveLargestPlan`.
			return offeredPlanId(resolution.planOffer) === null ? null : "plan";
		case "clear":
		case "upgradeOnly":
		case "checkingPurchase":
		case "quoting":
		case "signInRequired":
		case "paid":
		case "fileListRefused":
		case "aboveLargestPlan":
			// None of these offers a control here. `checkingPurchase` and `quoting` resolve themselves;
			// `clear`, `paid` and `upgradeOnly` are answers rather than obstacles. The panel's own upgrade nudge
			// speaks on `upgradeOnly` alone (see `overLimitShowsUpgradeNudge`). `signInRequired` waits on the
			// sender, and its way forward is the usual Send, which this rung leaves open
			// (kitecorp/transferify-ui#3435). `fileListRefused` names its own way forward, a smaller or zipped
			// selection, which the sender makes in the file list.
			// `aboveLargestPlan` does the same: its way forward is a smaller selection, which its sentence names.
			return null;
	}
}

/**
 * Whether the ordinary UPGRADE nudge is the honest answer for this resolution.
 *
 * True wherever there is no checkout for it to compete with. The owner's constraint is "no subscription
 * pitch competing AT THIS CHECKOUT" — so it binds `quoting` and `paid`, where a purchase is on offer or in
 * flight, and equally `quoteFailed`, where the answer is one sender action away and that action is the single
 * CTA on the strip. It does not bind `upgradeOnly`, a paid plan over its own cap, where nothing is for sale and the
 * upgrade is the only way forward.
 *
 * `signInRequired` gets no nudge either: a signed-out sender sees what an under-limit one sees
 * (kitecorp/transferify-ui#3435), and the price, after the sign-in, comes with no subscription pitch beside it.
 *
 * `fileListRefused` gets no nudge although nothing is for sale there either: no plan lifts a file-list rule, so
 * the nudge would point at a remedy that does not exist, and the rung already names the one that does.
 *
 * `planRequired` gets none either: its plan is the wall's own press, so a second pitch beside it would be the
 * competing offer the owner ruled out, and without the row there is no plan to point at.
 */
export function overLimitShowsUpgradeNudge(resolution: OverLimitResolution): boolean {
	return resolution.kind === "upgradeOnly";
}

/**
 * Whether this resolution STOPS the send.
 *
 * `paid` and `clear` pass; so does `signInRequired`, because for a signed-out sender the usual Send is
 * the way to the price (owner decision 2026-10-09, kitecorp/transferify-ui#3435): it opens the e-mail code
 * step, which signs them in, and it creates nothing (`advanceTransferCreation` answers a confirmed code with
 * `readyToResubmit`). Every other rung blocks — including `quoting`, because a send launched while the
 * price is still outstanding would race the answer. Keeping the refusal is half of ui#1454: the block
 * became a priced path, it did not go away, and an unpaid over-cap submit is still refused once the sender
 * has an account to pay with.
 */
export function overLimitBlocksSend(resolution: OverLimitResolution): boolean {
	return resolution.kind !== "clear" && resolution.kind !== "paid" && resolution.kind !== "signInRequired";
}

/**
 * Object-stable equality for a re-read allowance.
 *
 * A read that changes nothing must not hand the composer a fresh object, or the panel re-renders for no
 * reason, the churn ui#824 removed, so the hook keeps the previous reference when nothing changed.
 */
export function sameOverLimitAllowance(
	a: OverLimitAllowance | null,
	b: OverLimitAllowance | null,
): boolean {
	if (a === null || b === null) {
		return a === b;
	}
	return a.allowanceId === b.allowanceId && a.allowanceBytes === b.allowanceBytes
		&& a.amountMinor === b.amountMinor && a.currency === b.currency;
}

/**
 * Whether the over-limit CHECKOUT is on screen, which suppresses every subscription pitch on the panel
 * (kitecorp/transferify-ui#1597, enforcing kitecorp/transferify-ui#1454).
 *
 * The owner's constraint is "NO subscription pitch competing at this checkout. Single CTA, clean." —
 * and it binds exactly the rungs where a purchase is on offer or in flight: `quoting` and `paid`, the
 * same rungs {@link overLimitShowsUpgradeNudge}'s javadoc already names as the checkout.
 * On every other rung nothing is for sale here, and the offer callouts may speak.
 *
 * This predicate exists so the SUPPRESSION and the NOTICE cannot disagree about what "at this
 * checkout" means: both read the same resolution, and a new rung added to the union must be classified
 * here before the panel compiles a decision out of it.
 *
 * `checkingPurchase` belongs here for both of its outcomes: the sender either already holds a purchase
 * for this send or is about to be offered one, and a subscription pitch competes with either.
 *
 * `signInRequired` does not: nothing renders for it (kitecorp/transferify-ui#3435), so the panel speaks to a
 * signed-out sender over the limit exactly as it speaks to one under it.
 *
 * `planRequired` does, where the api offered a plan: the plan is on offer there, and the offer callouts must not
 * compete with it. Without the row nothing is for sale, as on `aboveLargestPlan`.
 */
export function overLimitCheckoutOnScreen(resolution: OverLimitResolution): boolean {
	return resolution.kind === "quoting" || resolution.kind === "paid"
		|| resolution.kind === "quoteFailed" || resolution.kind === "checkingPurchase"
		|| (resolution.kind === "planRequired" && offeredPlanId(resolution.planOffer) !== null);
}

/**
 * Which paid-moment surface this wall reports into (kitecorp/transferify-ui#1965): the two pricing
 * rules are two surfaces, because the hosted wall (a per-10 GB ladder, and the plan row beside it) and the Instant
 * wall (the plan alone since kitecorp/transferify-api-service#3005) convert differently and averaging them would
 * hide whichever one is failing.
 *
 * Structural return types rather than `upsell/offerEvents` imports, here and on the two helpers below:
 * this is a RULE module, and the owner's 2026-08-27 separation keeps rules from naming the emitter even
 * by type. The two literals are the api's `UpsellMoment` names, which is what makes them a contract.
 */
export function overLimitMoment(pricing: OverLimitPricing): "OVER_LIMIT_HOSTED" | "OVER_LIMIT_INSTANT" {
	return pricing === "instant" ? "OVER_LIMIT_INSTANT" : "OVER_LIMIT_HOSTED";
}

/**
 * The surface the SIGNED-OUT over-limit moment reports into (kitecorp/transferify-ui#3196), one per pricing rule for
 * the reason {@link overLimitMoment} splits the priced walls: the moment a sender reached decides which priced wall,
 * and which plan row, the sign-in leads to. Its showing ({@link overLimitSignedOutReached}) and its press (the sign-in
 * itself, {@link overLimitSignedOutSignInOpened}) go through the anonymous beacon; the sender has no account for the
 * signed-in one to write against. The api's moment names keep the word "wall" from when a strip rendered here.
 */
export function overLimitSignedOutMoment(
	pricing: OverLimitPricing,
): "OVER_LIMIT_HOSTED_SIGNED_OUT" | "OVER_LIMIT_INSTANT_SIGNED_OUT" {
	return pricing === "instant" ? "OVER_LIMIT_INSTANT_SIGNED_OUT" : "OVER_LIMIT_HOSTED_SIGNED_OUT";
}

/**
 * Whether the sender has reached the SIGNED-OUT over-limit moment right now (kitecorp/transferify-ui#3196): over the
 * cap with no account session. Nothing renders for it since kitecorp/transferify-ui#3435, and it is still counted as
 * shown, so the series the step-1 read began on 2026-10-05 keeps counting the senders who reach it. The rung and
 * nothing else, so a sender who signs in moves from this surface to the priced wall's in the same render that
 * resolves them.
 */
export function overLimitSignedOutReached(resolution: OverLimitResolution): boolean {
	return resolution.kind === "signInRequired";
}

/**
 * Whether the e-mail code step opening now is the signed-out moment's PRESS (kitecorp/transferify-ui#3435).
 *
 * With no strip, and so no sign-in button, the sign-in the sender starts from this moment is the e-mail code step
 * the usual Send opens, so its opening is what the press counts: once per opening, and only while the moment is
 * reached. A step that was already open, or that closes, is no press. Nor is the one a Request files press opens:
 * the send composer keeps its over-limit selection while the composer faces Request files, and that press opens the
 * same step for a sender who never pressed this Send.
 *
 * @param reached the moment is reached ({@link overLimitSignedOutReached}, with no session at all)
 * @param facing  which way the composer faces, so which press opened the step
 * @param wasOpen the code step was open on the previous reading
 * @param open    the code step is open now
 */
export function overLimitSignedOutSignInOpened(
	{reached, facing, wasOpen, open}: {reached: boolean; facing: ComposerMode; wasOpen: boolean; open: boolean},
): boolean {
	return reached && facing === "send" && open && !wasOpen;
}

/**
 * Whether the over-limit strip is a PAYWALL right now, to a sender the funnel can report on: a purchase
 * is on offer, in flight, or one sender action away (the quote retry).
 *
 * {@link overLimitCheckoutOnScreen} minus two rungs, each excluded for its own reason. `paid` is the
 * wall already climbed, and counting it as shown would make every paying sender appear twice in the
 * denominator. `checkingPurchase` is excluded because the account has not yet said whether it already
 * paid: counting it recorded the offer as shown again, a second after every refused upload start, for a
 * sender whose purchase still covered the send (kitecorp/transferify-ui#3010). The appearance begins when
 * the read answers that nothing is held.
 *
 * `signInRequired` is not a checkout on screen, and its sender has NO ACCOUNT for this funnel to count, so
 * the appearance begins after the sign-in, at `checkingPurchase`'s answer. Reaching it is counted on a surface
 * of its own instead, anonymously ({@link overLimitSignedOutReached}, kitecorp/transferify-ui#3196), and the
 * signed-in appearance that follows says it came from there. `aboveLargestPlan` is not an on-screen checkout
 * either — nothing is for sale there, so no purchase can follow and the funnel would carry a shown
 * that can never convert.
 */
export function overLimitWallShown(resolution: OverLimitResolution): boolean {
	return overLimitCheckoutOnScreen(resolution)
		&& resolution.kind !== "paid" && resolution.kind !== "checkingPurchase";
}

/**
 * Which wall stands in the way of this send (kitecorp/transferify-ui#3046).
 *
 * Two reasons stop a FREE send: the selection is over the free per-transfer cap (the SIZE wall, ui#1454), or the
 * account's live transfers already fill its free storage so the api refused the send (the STORAGE wall, the strip
 * ui#1664 added). A settled purchase pays for the send past either, because the api spends it the same way and a paid
 * transfer is exempt from both caps.
 */
export type PassWall = "size" | "storage";

/**
 * The wall a purchase on this composer is for, or null when neither stands.
 *
 * The size wall wins when both are up: the api refuses the size before it measures the storage, and a
 * size pass exempts the send from the storage cap as well, so it is the one purchase that clears both.
 * The storage wall exists on the HOSTED path only, because an instant transfer stores nothing.
 */
export function passWallFor(overCap: boolean, storageRefused: boolean, pricing: OverLimitPricing): PassWall | null {
	if (overCap) {
		return "size";
	}
	return storageRefused && pricing === "hosted" ? "storage" : null;
}

const CLEAR: OverLimitResolution = {kind: "clear"};

/**
 * The resolution as the SIZE wall's surfaces read it: the purchase's own answer while the size wall is up,
 * and `clear` otherwise.
 *
 * Everything the size wall owns reads this rather than the purchase's answer: its notice, its upgrade nudge,
 * its funnel, the pitches it suppresses, and the block on Send. The block is the reason this is a rule
 * rather than a condition at each reader. The storage wall must never hold Send shut: the sender may delete
 * a transfer to make room, and the api, not a quote asked before that delete, answers the next submit.
 */
export function sizeWallResolution(wall: PassWall | null, resolution: OverLimitResolution): OverLimitResolution {
	return wall === "size" ? resolution : CLEAR;
}

/**
 * Whether the storage strip says a held pass pays for this send (kitecorp/transferify-ui#3046): a purchase
 * bought before the one-off stopped being sold, which the next Send names and the api admits. No pass is
 * sold on the strip any more (kitecorp/transferify-ui#3916); its other two exits, deleting a transfer and
 * upgrading, are always there.
 */
export function storagePassPaid(wall: PassWall | null, resolution: OverLimitResolution): boolean {
	return wall === "storage" && resolution.kind === "paid";
}

/**
 * Whether the plan dialog should open by itself now, after a registration was refused for a plan on either wall
 * (`error.transfer.instant.planRequired`, kitecorp/transferify-api-service#3005 and kitecorp/transferify-ui#3781, or
 * `error.transfer.free.maxUpload` for a hosted upload, kitecorp/transferify-ui#3800).
 *
 * The refusal is the api's answer to a Send the strip let through. A quote or a purchase read still on its way is not a
 * cause: those rungs (`quoting`, `checkingPurchase`) hold Send shut ({@link overLimitBlocksSend}). The causes are a
 * wall that read `clear` or `paid` (the ui still takes the sender for a subscriber, or holds a purchase the api no longer
 * counts) and the signed-out rung, which leaves Send open because the sign-in comes first. The sender asked for the
 * send and is met with the plans, the smallest fitting one chosen, instead of a sentence. Only once (the caller clears the refusal
 * when it acts, and an open dialog is not opened again), and only once the wall holds an offered plan, which may be
 * later than the refusal (the account and the quote are read again). Where the wall never does, for instance a tier
 * the ui has not re-read, nothing opens and no message is shown: the refusal is not a toast (kitecorp/transferify-ui#3781).
 *
 * @param refused    the last registration was refused for a plan and the refusal has not been acted on
 * @param resolution what the over-cap selection means now
 * @param open       the pay dialog is already on screen
 */
export function planDialogOpensAfterRefusal(refused: boolean, resolution: OverLimitResolution, open: boolean): boolean {
	return refused && !open && resolution.kind === "planRequired" && offeredPlanId(resolution.planOffer) !== null;
}

/**
 * Whether closing the payment sheet is the SIZE wall's dismissal (owner decision 10 of kitecorp/transferify-ui#3022):
 * every close while the size wall is up is, and a close after the wall went away (the selection dropped back under
 * the cap) is not. The sheet opens on the size wall alone since the storage pass stopped being sold
 * (kitecorp/transferify-ui#3916).
 */
export function sheetCloseDismissesWall(wall: PassWall | null): boolean {
	return wall === "size";
}
